<template>
  <div class="m-3 pt-3 text-gray-800 dark:text-dark-text bg-white dark:bg-dark-800">
    <WarningText v-if="isAiReview(entryId).value" :text="AIWARNTEXT" />
    <div class="w-full">
      <ReviewDetailViewer :entryId="entryId" :entryType="entryType" :index="index" />
    </div>
    <div class="text-center">
      <div v-if="entryType === 'OPEN'" class="flex flex-wrap items-center justify-center my-5">
        <IconTextButton
          :border_radius="2"
          class="mx-1 my-1"
          :font_weight="'700'"
          :icon="'thumb_up_alt'"
          :iconColor="'text-green-400'"
          :iconSize="'text-xl'"
          :padding_bottom="4"
          :padding_left="12"
          :padding_right="12"
          :padding_top="4"
          :text="'5'"
          :textSize="'text-base'"
          @click="clickEvaluationReview(reviewId, 5)"
        />
        <IconTextButton
          :border_radius="2"
          class="mx-1 my-1"
          :font_weight="'700'"
          :icon="'thumb_up_alt'"
          :iconSize="'text-xl'"
          :padding_bottom="4"
          :padding_left="12"
          :padding_right="12"
          :padding_top="4"
          :text="'4'"
          :textSize="'text-base'"
          @click="clickEvaluationReview(reviewId, 4)"
        />
        <IconTextButton
          :border_radius="2"
          class="mx-1 my-1"
          :font_weight="'700'"
          :icon="'thumbs_up_down'"
          :iconSize="'text-xl'"
          :padding_bottom="4"
          :padding_left="12"
          :padding_right="12"
          :padding_top="4"
          :text="'3'"
          :textSize="'text-base'"
          @click="clickEvaluationReview(reviewId, 3)"
        />
        <IconTextButton
          :border_radius="2"
          class="mx-1 my-1"
          :font_weight="'700'"
          :icon="'thumb_down_alt'"
          :iconSize="'text-xl'"
          :padding_bottom="4"
          :padding_left="12"
          :padding_right="12"
          :padding_top="4"
          :text="'2'"
          :textSize="'text-base'"
          @click="clickEvaluationReview(reviewId, 2)"
        />
        <IconTextButton
          :border_radius="2"
          class="mx-1 my-1"
          :font_weight="'700'"
          :icon="'thumb_down_alt'"
          :iconColor="'text-red-400'"
          :iconSize="'text-xl'"
          :padding_bottom="4"
          :padding_left="12"
          :padding_right="12"
          :padding_top="4"
          :text="'1'"
          :textSize="'text-base'"
          @click="clickEvaluationReview(reviewId, 1)"
        />
      </div>
    </div>
    <div class="flex items-center justify-center">
      <div class="text-center flex-grow mb-5">
        <IconTextButton
          v-if="entryType === 'OPEN'"
          :border_radius="2"
          :font_weight="'700'"
          :icon="'cancel'"
          :iconSize="'text-2xl'"
          :padding_bottom="4"
          :padding_left="12"
          :padding_right="12"
          :padding_top="4"
          :text="$t(`message.closeEntry`)"
          :textSize="'text-base'"
          @click="clickCloseEntry()"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from 'vue'

import IconTextButton from '@presentation/components/Button/IconTextButton.vue'
import ReviewDetailViewer from '@presentation/components/Review/children/ReviewDetailViewer.vue'
import WarningText from '@presentation/components/Text/WarningText.vue'

import { useAuthStatePresenter } from '@/presentation/composables/auth/useAuthStatePresenter'
import { useConfigStatePresenter } from '@/presentation/composables/config/useConfigStatePresenter'
import { useNotificationPresenter } from '@/presentation/composables/notification/useNotificationPresenter'
import { useProblemStatePresenter } from '@/presentation/composables/problem/useProblemStatePresenter'
import { useReviewPresenter } from '@/presentation/composables/review/useReviewPresenter'
import timestampToDate from '@/util/date/timestampToDate'

export default defineComponent({
  components: {
    IconTextButton,
    WarningText,
    ReviewDetailViewer
  },
  props: {
    entryId: {
      type: String,
      required: true
    },
    entryType: {
      type: String,
      required: true
    },
    index: {
      type: Number,
      required: true
    }
  },
  setup(props, ctx) {
    const authStatePresenter = useAuthStatePresenter()
    const { problemId } = useProblemStatePresenter()
    const { displayLanguage } = useConfigStatePresenter()
    const { closeEntryNotification, evaluateNotification } = useNotificationPresenter()
    const { initState, getEntryDetail, isAiReview, postCloseEntry, postEvaluationReview } = useReviewPresenter()

    const userId = authStatePresenter.user.value.id
    const reviewId = computed(() => getEntryDetail(props.entryId).value.reviewId)
    const reviewDate = computed(() => getEntryDetail(props.entryId).value.reviewDate)

    const clickEvaluationReview = async (reviewId: string, score: number) => {
      await postEvaluationReview(reviewId, score)
      evaluateNotification(score)
    }

    const clickCloseEntry = async () => {
      await postCloseEntry(props.entryId)
      await initState(userId, problemId.value)
      closeEntryNotification()
    }

    const AIWARNTEXT =
      displayLanguage.value === 'Japanese' ? 'このレビューはAIが行いました。' : 'This review was performed by AI.'

    return {
      timestampToDate,
      initState,
      isAiReview,
      clickEvaluationReview,
      clickCloseEntry,
      reviewId,
      reviewDate,
      AIWARNTEXT
    }
  }
})
</script>

<style lang="postcss" scoped>
.panel-top {
  padding: 0.7em 1em 0.7em 1em;
}

.panel-bottom {
  padding: 0em 0em 0em 0em;
}

.no-select {
  user-select: none;
}
</style>
